// src/services/storageService.js - Saved data on this device
//
// The app calls these with the preference 'local' only (context/AppContext.jsx).
// Cloud backup goes to the person's DDD account in the Auth Hub through
// services/hubSync.js, never to this app's own Firestore. The 'cloud' and
// 'both' branches below are what the old backup used; nothing calls them
// with those preferences any more. Don't reintroduce them: data written to
// this app's own project is out of reach of the hub's account deletion.

import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

// ============================================================================
// ASSESSMENT STORAGE
// ============================================================================

export const saveAssessmentResults = async (userId, assessmentData, storagePreference = 'both') => {
  try {
    // Save to localStorage if preference allows
    if (storagePreference === 'local' || storagePreference === 'both') {
      localStorage.setItem('relationshipCompass_assessmentResults', JSON.stringify(assessmentData));
    }

    // Save to Firestore if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const assessmentRef = doc(db, 'users', userId, 'data', 'assessment');
      await setDoc(assessmentRef, {
        ...assessmentData,
        updatedAt: serverTimestamp(),
        savedAt: new Date().toISOString()
      }, { merge: true });
    }

    console.log('✅ Assessment results saved successfully');
    return true;
  } catch (error) {
    console.error('❌ Error saving assessment results:', error);
    return false;
  }
};

export const loadAssessmentResults = async (userId, storagePreference = 'both') => {
  try {
    // Try cloud first if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const assessmentRef = doc(db, 'users', userId, 'data', 'assessment');
      const assessmentDoc = await getDoc(assessmentRef);

      if (assessmentDoc.exists()) {
        const data = assessmentDoc.data();
        console.log('✅ Loaded assessment from Firebase');

        // Sync to local if 'both'
        if (storagePreference === 'both') {
          localStorage.setItem('relationshipCompass_assessmentResults', JSON.stringify(data));
        }

        return data;
      }
    }

    // Fall back to localStorage
    if (storagePreference === 'local' || storagePreference === 'both') {
      const localData = localStorage.getItem('relationshipCompass_assessmentResults');
      if (localData) {
        console.log('✅ Loaded assessment from localStorage');
        return JSON.parse(localData);
      }
    }

    return null;
  } catch (error) {
    console.error('❌ Error loading assessment results:', error);

    // Try localStorage as fallback
    const localData = localStorage.getItem('relationshipCompass_assessmentResults');
    return localData ? JSON.parse(localData) : null;
  }
};

// ============================================================================
// MODULE PROGRESS STORAGE
// ============================================================================

export const saveModuleProgress = async (userId, moduleId, progressData, storagePreference = 'both') => {
  try {
    // Save to localStorage if preference allows
    if (storagePreference === 'local' || storagePreference === 'both') {
      const allProgress = JSON.parse(localStorage.getItem('relationshipCompass_moduleProgress') || '{}');
      allProgress[moduleId] = {
        ...progressData,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('relationshipCompass_moduleProgress', JSON.stringify(allProgress));
    }

    // Save to Firestore if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const progressRef = doc(db, 'users', userId, 'moduleProgress', moduleId);
      await setDoc(progressRef, {
        ...progressData,
        updatedAt: serverTimestamp(),
        savedAt: new Date().toISOString()
      }, { merge: true });

      // Update user stats
      await updateUserStats(userId, moduleId, progressData);
    }

    console.log(`✅ Module ${moduleId} progress saved successfully`);
    return true;
  } catch (error) {
    console.error(`❌ Error saving module ${moduleId} progress:`, error);
    return false;
  }
};

export const loadModuleProgress = async (userId, storagePreference = 'both') => {
  try {
    // Try cloud first if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const progressCollection = collection(db, 'users', userId, 'moduleProgress');
      const snapshot = await getDocs(progressCollection);

      if (!snapshot.empty) {
        const progress = {};
        snapshot.forEach(doc => {
          progress[doc.id] = doc.data();
        });

        console.log('✅ Loaded module progress from Firebase');

        // Sync to local if 'both'
        if (storagePreference === 'both') {
          localStorage.setItem('relationshipCompass_moduleProgress', JSON.stringify(progress));
        }

        return progress;
      }
    }

    // Fall back to localStorage
    if (storagePreference === 'local' || storagePreference === 'both') {
      const localData = localStorage.getItem('relationshipCompass_moduleProgress');
      if (localData) {
        console.log('✅ Loaded module progress from localStorage');
        return JSON.parse(localData);
      }
    }

    return {};
  } catch (error) {
    console.error('❌ Error loading module progress:', error);

    // Try localStorage as fallback
    const localData = localStorage.getItem('relationshipCompass_moduleProgress');
    return localData ? JSON.parse(localData) : {};
  }
};

const updateUserStats = async (userId, moduleId, progress) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const stats = userDoc.data().stats || {};

      // Update completion stats
      if (progress.completed && !stats.completedModules?.includes(moduleId)) {
        await updateDoc(userRef, {
          'stats.completedModules': [...(stats.completedModules || []), moduleId],
          'stats.totalModulesCompleted': (stats.totalModulesCompleted || 0) + 1,
          'stats.lastActivityAt': serverTimestamp()
        });
      }
    } else {
      // Create user stats document if it doesn't exist
      await setDoc(userRef, {
        stats: {
          completedModules: progress.completed ? [moduleId] : [],
          totalModulesCompleted: progress.completed ? 1 : 0,
          createdAt: serverTimestamp(),
          lastActivityAt: serverTimestamp()
        }
      });
    }
  } catch (error) {
    console.error('Error updating user stats:', error);
  }
};

// ============================================================================
// TOOL DATA STORAGE
// ============================================================================

export const saveToolData = async (userId, toolId, toolData, storagePreference = 'both') => {
  try {
    // Save to localStorage if preference allows
    if (storagePreference === 'local' || storagePreference === 'both') {
      const allToolData = JSON.parse(localStorage.getItem('relationshipCompass_toolData') || '{}');
      allToolData[toolId] = {
        ...toolData,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('relationshipCompass_toolData', JSON.stringify(allToolData));
    }

    // Save to Firestore if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const toolRef = doc(db, 'users', userId, 'toolData', toolId);
      await setDoc(toolRef, {
        ...toolData,
        updatedAt: serverTimestamp(),
        savedAt: new Date().toISOString()
      }, { merge: true });
    }

    console.log(`✅ Tool ${toolId} data saved successfully`);
    return true;
  } catch (error) {
    console.error(`❌ Error saving tool ${toolId} data:`, error);
    return false;
  }
};

export const loadToolData = async (userId, storagePreference = 'both') => {
  try {
    // Try cloud first if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const toolCollection = collection(db, 'users', userId, 'toolData');
      const snapshot = await getDocs(toolCollection);

      if (!snapshot.empty) {
        const toolData = {};
        snapshot.forEach(doc => {
          toolData[doc.id] = doc.data();
        });

        console.log('✅ Loaded tool data from Firebase');

        // Sync to local if 'both'
        if (storagePreference === 'both') {
          localStorage.setItem('relationshipCompass_toolData', JSON.stringify(toolData));
        }

        return toolData;
      }
    }

    // Fall back to localStorage
    if (storagePreference === 'local' || storagePreference === 'both') {
      const localData = localStorage.getItem('relationshipCompass_toolData');
      if (localData) {
        console.log('✅ Loaded tool data from localStorage');
        return JSON.parse(localData);
      }
    }

    return {};
  } catch (error) {
    console.error('❌ Error loading tool data:', error);

    // Try localStorage as fallback
    const localData = localStorage.getItem('relationshipCompass_toolData');
    return localData ? JSON.parse(localData) : {};
  }
};

export const saveToolUsage = async (userId, toolId, usageData, storagePreference = 'both') => {
  try {
    // Save to localStorage if preference allows
    if (storagePreference === 'local' || storagePreference === 'both') {
      const allUsage = JSON.parse(localStorage.getItem('relationshipCompass_toolUsage') || '{}');
      allUsage[toolId] = {
        ...usageData,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('relationshipCompass_toolUsage', JSON.stringify(allUsage));
    }

    // Save to Firestore if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const usageRef = doc(db, 'users', userId, 'toolUsage', toolId);
      await setDoc(usageRef, {
        ...usageData,
        updatedAt: serverTimestamp(),
        savedAt: new Date().toISOString()
      }, { merge: true });
    }

    return true;
  } catch (error) {
    console.error(`❌ Error saving tool ${toolId} usage:`, error);
    return false;
  }
};

export const loadToolUsage = async (userId, storagePreference = 'both') => {
  try {
    // Try cloud first if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const usageCollection = collection(db, 'users', userId, 'toolUsage');
      const snapshot = await getDocs(usageCollection);

      if (!snapshot.empty) {
        const usage = {};
        snapshot.forEach(doc => {
          usage[doc.id] = doc.data();
        });

        console.log('✅ Loaded tool usage from Firebase');

        // Sync to local if 'both'
        if (storagePreference === 'both') {
          localStorage.setItem('relationshipCompass_toolUsage', JSON.stringify(usage));
        }

        return usage;
      }
    }

    // Fall back to localStorage
    if (storagePreference === 'local' || storagePreference === 'both') {
      const localData = localStorage.getItem('relationshipCompass_toolUsage');
      if (localData) {
        console.log('✅ Loaded tool usage from localStorage');
        return JSON.parse(localData);
      }
    }

    return {};
  } catch (error) {
    console.error('❌ Error loading tool usage:', error);

    // Try localStorage as fallback
    const localData = localStorage.getItem('relationshipCompass_toolUsage');
    return localData ? JSON.parse(localData) : {};
  }
};

// ============================================================================
// DATA LOADING & MIGRATION
// ============================================================================

export const loadAllUserData = async (userId, storagePreference = 'both') => {
  try {
    console.log('📥 Loading all user data...');

    const [assessment, moduleProgress, toolData, toolUsage, gamification] = await Promise.all([
      loadAssessmentResults(userId, storagePreference),
      loadModuleProgress(userId, storagePreference),
      loadToolData(userId, storagePreference),
      loadToolUsage(userId, storagePreference),
      loadGamificationData(userId, storagePreference)
    ]);

    console.log('✅ All user data loaded successfully');

    return {
      assessmentResults: assessment,
      moduleProgress: moduleProgress,
      toolData: toolData,
      toolUsage: toolUsage,
      gamification: gamification
    };
  } catch (error) {
    console.error('❌ Error loading all user data:', error);
    return {
      assessmentResults: null,
      moduleProgress: {},
      toolData: {},
      toolUsage: {},
      gamification: {
        totalPoints: 0,
        unlockedAchievements: [],
        streaks: {
          currentLoginStreak: 0,
          longestLoginStreak: 0,
          lastLoginDate: null,
          weeklyChallengeStreak: 0
        },
        lastPointsAwarded: []
      }
    };
  }
};

// ============================================================================
// GAMIFICATION STORAGE
// ============================================================================

export const saveGamificationData = async (userId, gamificationData, storagePreference = 'both') => {
  try {
    // Save to localStorage if preference allows
    if (storagePreference === 'local' || storagePreference === 'both') {
      localStorage.setItem('relationshipCompass_gamification', JSON.stringify(gamificationData));
    }

    // Save to Firestore if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const gamificationRef = doc(db, 'users', userId, 'data', 'gamification');
      await setDoc(gamificationRef, {
        ...gamificationData,
        updatedAt: serverTimestamp(),
        savedAt: new Date().toISOString()
      }, { merge: true });
    }

    console.log('✅ Gamification data saved successfully');
    return true;
  } catch (error) {
    console.error('❌ Error saving gamification data:', error);
    return false;
  }
};

export const loadGamificationData = async (userId, storagePreference = 'both') => {
  try {
    // Try cloud first if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const gamificationRef = doc(db, 'users', userId, 'data', 'gamification');
      const gamificationDoc = await getDoc(gamificationRef);

      if (gamificationDoc.exists()) {
        const data = gamificationDoc.data();
        console.log('✅ Loaded gamification data from Firebase');

        // Sync to local if 'both'
        if (storagePreference === 'both') {
          localStorage.setItem('relationshipCompass_gamification', JSON.stringify(data));
        }

        return data;
      }
    }

    // Fall back to localStorage
    if (storagePreference === 'local' || storagePreference === 'both') {
      const localData = localStorage.getItem('relationshipCompass_gamification');
      if (localData) {
        console.log('✅ Loaded gamification data from localStorage');
        return JSON.parse(localData);
      }
    }

    // Return default gamification data structure
    return {
      totalPoints: 0,
      unlockedAchievements: [],
      streaks: {
        currentLoginStreak: 0,
        longestLoginStreak: 0,
        lastLoginDate: null,
        weeklyChallengeStreak: 0
      },
      lastPointsAwarded: []
    };
  } catch (error) {
    console.error('❌ Error loading gamification data:', error);

    // Try localStorage as fallback
    const localData = localStorage.getItem('relationshipCompass_gamification');
    if (localData) {
      return JSON.parse(localData);
    }

    // Return default data
    return {
      totalPoints: 0,
      unlockedAchievements: [],
      streaks: {
        currentLoginStreak: 0,
        longestLoginStreak: 0,
        lastLoginDate: null,
        weeklyChallengeStreak: 0
      },
      lastPointsAwarded: []
    };
  }
};

// ============================================================================
// DATA CLEANUP
// ============================================================================

export const clearLocalData = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('relationshipCompass_')) {
        localStorage.removeItem(key);
      }
    });
    console.log('✅ Local data cleared');
    return true;
  } catch (error) {
    console.error('❌ Error clearing local data:', error);
    return false;
  }
};

// ============================================================================
// MIGRATION HELPERS
// ============================================================================

export const migrateOldLocalStorage = () => {
  try {
    // Migrate old artOfStory_ keys to relationshipCompass_ keys
    const keys = Object.keys(localStorage);
    let migrated = 0;

    keys.forEach(key => {
      if (key.startsWith('artOfStory_')) {
        const newKey = key.replace('artOfStory_', 'relationshipCompass_');
        const value = localStorage.getItem(key);
        localStorage.setItem(newKey, value);
        localStorage.removeItem(key);
        migrated++;
      }
    });

    if (migrated > 0) {
      console.log(`✅ Migrated ${migrated} localStorage keys from artOfStory_ to relationshipCompass_`);
    }

    return migrated;
  } catch (error) {
    console.error('❌ Error migrating localStorage:', error);
    return 0;
  }
};
